import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  TrendingUp,
  ShoppingBag,
  MessageSquare,
  Users,
  Package as PackageIcon,
  Mail,
} from "lucide-react";
import { api, formatPrice } from "../lib/api";
import AdminLayout from "../components/AdminLayout";
import StripePayoutsWidget from "../components/StripePayoutsWidget";

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/admin/dashboard");
        if (!cancelled) setData(r.data);
      } catch (_) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <AdminLayout
      title="Dashboard."
      action={
        <Link to="/admin/products" className="btn-primary" data-testid="dashboard-manage-cta">
          Manage Inventory <ArrowUpRight size={16} strokeWidth={1.5} />
        </Link>
      }
    >
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-40 bg-neutral-200/60 animate-pulse" />
          ))}
        </div>
      ) : !data ? (
        <p className="font-display text-2xl text-[var(--mn-ink-2)]" data-testid="dashboard-error">
          Could not load dashboard data.
        </p>
      ) : (
        <Body data={data} />
      )}
    </AdminLayout>
  );
}

function Body({ data }) {
  const k = data.kpis;
  return (
    <div data-testid="dashboard-body">
      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-12">
        <Kpi
          icon={<TrendingUp size={18} strokeWidth={1.4} />}
          eyebrow="Today"
          headline={formatPrice(k.today_revenue)}
          sub={`${k.today_orders} order${k.today_orders === 1 ? "" : "s"}`}
          testid="kpi-today"
        />
        <Kpi
          icon={<ShoppingBag size={18} strokeWidth={1.4} />}
          eyebrow="This Month"
          headline={formatPrice(k.month_revenue)}
          sub={`${k.month_orders} order${k.month_orders === 1 ? "" : "s"}`}
          testid="kpi-month"
          chart={<Sparkline series={k.month_revenue_series || []} />}
        />
        <Kpi
          icon={<MessageSquare size={18} strokeWidth={1.4} />}
          eyebrow="Style Enquiries"
          headline={String(k.new_enquiries)}
          sub={`${k.total_enquiries} all-time`}
          accent={k.new_enquiries > 0}
          testid="kpi-enquiries"
        />
        <Kpi
          icon={<Users size={18} strokeWidth={1.4} />}
          eyebrow="Subscribers"
          headline={String(k.newsletter_subscribers)}
          sub="Newsletter list"
          testid="kpi-subscribers"
        />
      </div>

      {/* Secondary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 mb-12 border-t border-[var(--mn-border)] pt-8">
        <SecondaryStat label="All-time revenue" value={formatPrice(k.all_time_revenue)} testid="kpi-alltime-rev" />
        <SecondaryStat label="All-time orders" value={String(k.all_time_orders)} testid="kpi-alltime-orders" />
        <SecondaryStat label="Active products" value={String(k.products)} testid="kpi-products" link="/admin/products" />
      </div>

      {/* Treasury widget */}
      <div className="mb-16">
        <StripePayoutsWidget />
      </div>

      {/* Feeds */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <FeedSection
          title="Recent orders"
          eyebrow="Last 5"
          empty="No orders yet."
          testid="feed-orders"
          items={data.recent_orders}
          renderItem={(o) => (
            <FeedRow
              key={o.id}
              left={
                <>
                  <p className="font-display text-xl tracking-tight">{o.order_number}</p>
                  <p className="text-xs text-[var(--mn-ink-2)] mt-1">
                    {o.shipping?.full_name} · {o.items.length} item{o.items.length === 1 ? "" : "s"}
                  </p>
                </>
              }
              right={
                <>
                  <p className="font-mono-luxe text-sm">{formatPrice(o.total)}</p>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--mn-muted)] mt-1">
                    {timeAgo(o.created_at)}
                  </p>
                </>
              }
            />
          )}
        />
        <FeedSection
          title="Recent style enquiries"
          eyebrow="Last 5"
          empty="No enquiries yet."
          testid="feed-enquiries"
          actionLink={{ to: "/admin/enquiries", label: "Open inbox" }}
          items={data.recent_enquiries}
          renderItem={(e) => (
            <FeedRow
              key={e.id}
              left={
                <>
                  <p className="font-display text-xl tracking-tight">{e.full_name}</p>
                  <p className="text-xs text-[var(--mn-ink-2)] mt-1 truncate max-w-[28ch]">
                    {e.interested_piece || e.message || e.email}
                  </p>
                </>
              }
              right={
                <>
                  <a
                    href={`mailto:${e.email}`}
                    className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.22em] text-[var(--mn-ink-2)] hover:text-[var(--mn-accent)]"
                    data-testid={`reply-${e.id}`}
                  >
                    <Mail size={12} strokeWidth={1.5} /> Reply
                  </a>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--mn-muted)] mt-1">
                    {timeAgo(e.created_at)}
                  </p>
                </>
              }
            />
          )}
        />
      </div>
    </div>
  );
}

function Kpi({ icon, eyebrow, headline, sub, accent = false, testid, chart = null }) {
  return (
    <div
      className={`border ${accent ? "border-[var(--mn-accent)]" : "border-[var(--mn-ink)]"} p-6 md:p-8 transition-colors hover:bg-[var(--mn-ink)] hover:text-[var(--mn-bg)] group relative overflow-hidden`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <p className="eyebrow group-hover:text-[var(--mn-bg)]/60">{eyebrow}</p>
        <span className="text-[var(--mn-ink-2)] group-hover:text-[var(--mn-bg)]/70">{icon}</span>
      </div>
      <p className="font-display text-3xl md:text-4xl mt-4 tracking-tighter leading-none">{headline}</p>
      <p className="text-xs text-[var(--mn-ink-2)] mt-3 group-hover:text-[var(--mn-bg)]/60">{sub}</p>
      {chart && <div className="mt-4 -mx-1" data-testid={`${testid}-chart`}>{chart}</div>}
    </div>
  );
}

/**
 * Tiny inline SVG sparkline. Renders a polyline of the daily revenue
 * series for the current month plus a translucent fill. Designed to live
 * inside a KPI tile; fully responsive via viewBox.
 */
function Sparkline({ series, height = 36 }) {
  if (!series || series.length === 0) {
    return <div className="h-9 text-[10px] uppercase tracking-[0.22em] text-[var(--mn-muted)] group-hover:text-[var(--mn-bg)]/40">No revenue yet this month</div>;
  }
  const values = series.map((d) => Number(d.revenue) || 0);
  const max = Math.max(...values, 1);
  const w = 100; // viewBox width — scales to container
  const h = height;
  const stepX = series.length > 1 ? w / (series.length - 1) : 0;
  const padY = 2;
  const usableH = h - padY * 2;
  const points = values.map((v, i) => {
    const x = series.length === 1 ? w / 2 : i * stepX;
    const y = padY + (usableH - (v / max) * usableH);
    return [x, y];
  });
  const linePath = points.map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`).join(" ");
  const fillPath = `${linePath} L ${w} ${h} L 0 ${h} Z`;
  const last = points[points.length - 1];

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      className="w-full h-9"
      data-testid="sparkline-svg"
      aria-label="Daily revenue this month"
    >
      <path d={fillPath} fill="currentColor" opacity="0.08" />
      <path d={linePath} fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
      {last && (
        <circle cx={last[0]} cy={last[1]} r="1.6" fill="currentColor" />
      )}
    </svg>
  );
}

function SecondaryStat({ label, value, link, testid }) {
  const Inner = (
    <div className="flex items-baseline justify-between gap-4 py-3" data-testid={testid}>
      <span className="eyebrow">{label}</span>
      <span className="font-mono-luxe text-lg">{value}</span>
    </div>
  );
  return link ? <Link to={link} className="block hover:bg-neutral-100 px-2 -mx-2">{Inner}</Link> : Inner;
}

function FeedSection({ title, eyebrow, items, empty, renderItem, testid, actionLink }) {
  return (
    <section data-testid={testid}>
      <div className="flex items-end justify-between mb-6 gap-4">
        <div>
          <p className="eyebrow">{eyebrow}</p>
          <h2 className="font-display text-3xl md:text-4xl mt-2 tracking-tighter">{title}</h2>
        </div>
        {actionLink && (
          <Link
            to={actionLink.to}
            className="text-xs uppercase tracking-[0.22em] inline-flex items-center gap-1.5 hover:text-[var(--mn-accent)] whitespace-nowrap"
            data-testid={`${testid}-action`}
          >
            {actionLink.label} <ArrowUpRight size={14} strokeWidth={1.5} />
          </Link>
        )}
      </div>
      {items.length === 0 ? (
        <div className="border border-dashed border-[var(--mn-border-strong)] py-16 text-center">
          <p className="text-[var(--mn-ink-2)]">{empty}</p>
        </div>
      ) : (
        <ul className="border-y border-[var(--mn-border)] divide-y divide-[var(--mn-border)]">
          {items.map(renderItem)}
        </ul>
      )}
    </section>
  );
}

function FeedRow({ left, right }) {
  return (
    <li className="py-5 flex items-start justify-between gap-6">
      <div className="min-w-0">{left}</div>
      <div className="text-right shrink-0">{right}</div>
    </li>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}
